import * as React from 'react';

// dayjs
import dayjs from 'dayjs';

// Mui Imports
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    LocalizationProvider,
    PickersDay,
    DatePickerToolbar,
    DatePicker
} from '@mui/x-date-pickers';
import { styled } from '@mui/material/styles';

// Local Component
import { SearchResultChip } from '../SearchResult/SearchResult';

// Typography
import { H6 } from '../../themes/typography';

// CSS Styles
import '../../styles/components/EventSchedule.css';

// Highlighted Days CSS
const HighlightedDay = styled(PickersDay)({
    border: '1px solid transparent',
    borderRadius: '50%',
    '& hover:': {
        borderColor: '#f8a21e'
    },
    '&.Mui-selected': {
        backgroundColor: '#f8a21e !important',
        color: '#f5f5f5 !important'
        // fontSize: '1rem' // optional
    }
});

// Highlighted Slots Logic
function HighlightedSlots({ selectedDates = [], day, ...other }) {
    const isSelected = selectedDates.includes(day.format('DD/MM/YYYY'));

    return <HighlightedDay {...other} day={day} selected={isSelected} />;
}

export default function MuiDatePicker({
    id = 'date-picker',
    name = 'datePicker',
    label = 'Mui Date Picker',
    value,
    error = false,
    fullWidth = false,
    required = false,
    onChange = () => {},
    disabled = false,
    readOnly = false,
    disableHighlightToday = false,
    helperText = '',
    minDate,
    maxDate,
    slots = {},
    slotProps = {},
    showToolbar = false,
    selectedDates = [],
    closeOnSelect = true,
    defaultValue,
    onDeleteDate,
    format = 'DD/MM/YYYY',
    onClose = () => {},
    onOpen = () => {},
    disablePast = true,
    disableFuture = false
}) {
    const handleDeleteDate = (date) => {
        if (onDeleteDate) {
            onDeleteDate(date); // Call the parent's deletion handler
        }
    };

    function showSelectedDates() {
        return (
            <div className="custom-date-picker-toolbar">
                <H6 fontWeight={500}>Selected Dates</H6>
                <div className="selected-dates-toolbar">
                    {selectedDates
                        .map((date) => dayjs(date, 'DD/MM/YYYY'))
                        .sort((a, b) => a - b)
                        .map((date, index) => (
                            <SearchResultChip
                                key={index}
                                chipName={date.format('DD/MM/YYYY')}
                                onDelete={() => handleDeleteDate(date.format('DD/MM/YYYY'))}
                            />
                        ))}
                </div>
            </div>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                // open
                closeOnSelect={closeOnSelect}
                defaultValue={defaultValue}
                label={label}
                format={format}
                value={dayjs(value)}
                reduceAnimations={true}
                disabled={disabled}
                disablePast={disablePast}
                readOnly={readOnly}
                onChange={onChange}
                disableHighlightToday={disableHighlightToday}
                minDate={minDate}
                maxDate={maxDate}
                onClose={onClose}
                onOpen={onOpen}
                disableFuture={disableFuture}
                slots={{
                    toolbar: showToolbar ? showSelectedDates : null,
                    day: HighlightedSlots,
                    ...slots
                }}
                displayStaticWrapperAs="mobile"
                orientation="landscape"
                slotProps={{
                    day: {
                        selectedDates
                    },
                    textField: {
                        helperText: helperText,
                        error: error,
                        fullWidth: fullWidth,
                        id: id,
                        name: name,
                        required: required
                    },
                    // toolbar: {
                    //     hidden: showToolbar ? false : true,
                    //     toolbarPlaceholder: showSelectedDates()
                    // },
                    actionBar: {
                        actions: showToolbar ? ['accept'] : [],
                        sx: {
                            position: 'absolute',
                            bottom: '0',
                            right: showToolbar ? '34%' : '0%',
                            color: '#f5f5f5',
                            '& .MuiButton-root': {
                                color: '#f5f5f5'
                            }
                        }
                    },
                    desktopPaper: {
                        sx: {
                            background: 'rgba(0, 0, 0, 0.2)',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            backdropFilter: 'blur(14px)',
                            margin: '0',
                            borderRadius: '15px',
                            color: '#f5f5f5',
                            // height: '320px',
                            fontFamily: 'Nunito, sans-serif !important',
                            '& .MuiTypography-root': {
                                color: '#26c1e8',
                                fontWeight: '600',
                                fontFamily: 'Nunito, sans-serif !important',
                                fontSize: '0.875rem'
                            },
                            '& .MuiPickersDay-root': {
                                color: '#f5f5f5',
                                fontFamily: 'Nunito, sans-serif !important',
                                fontSize: '0.875rem',
                                fontWeight: '700',
                                border: '2px solid transparent',
                                transition: 'all 0.1s ease-in-out',
                                '&.Mui-disabled': {
                                    color: 'rgba(255,255,255,0.3) !important',
                                    fontWeight: '500'
                                },
                                '&:hover': {
                                    // borderColor: '#f8a21e',
                                    color: '#26c1e8'
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#f8a21e !important',
                                    color: '#f5f5f5 !important'
                                    // fontSize: '1rem' // optional
                                }
                            },
                            '& .MuiIconButton-root': {
                                color: '#f8a21e !important'
                            },
                            '& .MuiPickersCalendarHeader-labelContainer': {
                                fontFamily: 'Nunito, sans-serif !important',
                                fontSize: '1.125rem'
                            },
                            '.css-qct7wd-MuiButtonBase-root-MuiPickersDay-root:not(.Mui-selected)':
                                {
                                    border: '2px solid #f5f5f5'
                                },
                            '& .MuiPickersLayout-root': {
                                display: 'flex',
                                flexDirection: 'row-reverse'
                            }
                        }
                    },
                    ...slotProps
                }}
                sx={{
                    minWidth: '265px',
                    '.css-1n04w30-MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                        {
                            borderColor: '#f5f5f5'
                        },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f5f5f5'
                    },
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '15px',
                        fontFamily: 'Nunito, sans-serif',
                        color: '#f5f5f5',
                        '& fieldset': {
                            borderColor: '#f5f5f5'
                        },
                        '&:hover fieldset': {
                            borderColor: '#f8a21e'
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#f8a21e'
                        },
                        '&.Mui-disabled': {
                            WebkitTextFillColor: '#f5f5f5',
                            '&:hover fieldset': {
                                borderColor: '#f5f5f5'
                            }
                        }
                    },
                    '& .MuiOutlinedInput-input': {
                        fontSize: '1.125rem'
                    },
                    '& .MuiInputLabel-root': {
                        color: '#f5f5f5 !important',
                        fontFamily: 'Noto Sans, sans-serif'
                    },
                    '& .MuiSvgIcon-root': {
                        fill: '#f5f5f5'
                    },
                    '& .MuiFormHelperText-root': {
                        color: '#f5f5f5',
                        margin: '6px 0px 0px 3px',
                        fontFamily: 'Nunito, sans-serif',
                        fontSize: '0.875rem',
                        lineHeight: 'none',
                        letterSpacing: '0px',
                        '&.Mui-error': {
                            color: '#f44336'
                        },
                        '&.Mui-disabled': {
                            color: '#f5f5f5'
                        }
                    }
                }}
            />
        </LocalizationProvider>
    );
}

// This is the responsive variant of mui library

// const SelectedDates = styled(DatePickerToolbar)({
//     minWidth: '170px',
//     maxWidth: 'fit-content',
//     color: 'white',
//     alignItems: 'center',
//     '& .MuiDatePickerToolbar-title': {
//         margin: '0',
//     },
//     '& .MuiPickersToolbar-content': {
//         maxHeight: '240px',
//         overflowY: 'scroll'
//     },
//     "& hover:": {
//         borderColor: '#f8a21e'
//     },
//     "&.Mui-selected": {
//         backgroundColor: '#f8a21e !important',
//         color: '#f5f5f5 !important',
//     },
// });
